import { useMemo, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const formatINR = (value) => {
  if (!value || isNaN(value)) return "—";
  if (Math.abs(value) >= 10000000) return `${(value / 10000000).toFixed(2)} Cr`;
  if (Math.abs(value) >= 100000) return `${(value / 100000).toFixed(1)} L`;
  return `${Math.round(value).toLocaleString("en-IN")}`;
};

const defaultVelocity = {
  // Down Payment Sales
  downPaymentPct: 10,        // % of total inventory
  downPaymentDiscount: 5,    // % discount on average rate
  // Regular Sales (first 6 months)
  regularSalesPct: 40,       // % of inventory sold in first 6 months
  regularDiscount: 0,        // % discount on rate
  regularMonths: 6,          // period for regular sales
  // Post-6-month Sales
  postReservePct: 10,        // % of inventory kept unsold/reserved
  postDiscount: 0,           // % discount (usually 0 or premium)
  // Payment Schedule (for regular & post sales)
  bookingPct: 45,            // % paid at booking (within 2 months)
  stagePct: 10,              // % per stage
  numStages: 5,              // number of stages (total = stagePct × numStages = 50%)
  possessionPct: 5,          // % on possession
};

const SaleVelocityForm = ({ formData, plData, expenseQuarters, expenseMonths, preProjectMonths }) => {
  const [velocity, setVelocity] = useState(() => {
    // Load from formData if saved
    return formData.saleVelocity ? { ...defaultVelocity, ...formData.saleVelocity } : { ...defaultVelocity };
  });

  const handleChange = useCallback((field, value) => {
    setVelocity(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  }, []);

  const incomeData = useMemo(() => {
    if (!plData || !expenseMonths || expenseMonths.length === 0) return null;

    const durationMonths = parseInt(formData.projectDuration) || 36;
    const totalMonths = preProjectMonths + durationMonths;
    const startIdx = preProjectMonths;
    const totalSaleableArea = plData?.finalSaleAreaSqft || 0;
    const avgRate = plData?.saleRatePerSqft || 0;

    if (totalSaleableArea <= 0 || avgRate <= 0) return null;

    // Developer's saleable area (after JV deduction for greenfield)
    const developerArea = plData?.developerSaleAreaSqft || totalSaleableArea;

    // ============ PHASE 1: DOWN PAYMENT SALES ============
    const dpArea = developerArea * (velocity.downPaymentPct / 100);
    const dpRate = avgRate * (1 - velocity.downPaymentDiscount / 100);
    const dpRevenue = dpArea * dpRate;
    // Full amount realized at project start (M1)

    // ============ PHASE 2: REGULAR SALES (First N months) ============
    const regArea = developerArea * (velocity.regularSalesPct / 100);
    const regRate = avgRate * (1 - velocity.regularDiscount / 100);
    const regRevenue = regArea * regRate;
    const regMonths = velocity.regularMonths || 6;
    // Sold equally across first N months
    const regPerMonth = regArea / regMonths;
    const regRevenuePerUnit = regRate; // per sqft

    // ============ PHASE 3: POST REGULAR SALES ============
    const soldPct = velocity.downPaymentPct + velocity.regularSalesPct;
    const remainingPct = Math.max(0, 100 - soldPct - velocity.postReservePct);
    const postArea = developerArea * (remainingPct / 100);
    const postRate = avgRate * (1 - velocity.postDiscount / 100);
    const postRevenue = postArea * postRate;
    const postStartMonth = startIdx + regMonths; // After regular sales period
    const postMonths = totalMonths - postStartMonth;
    const postQuarters = Math.max(1, Math.ceil(postMonths / 3));
    const postPerQuarter = postArea / postQuarters;

    // ============ PAYMENT SCHEDULE STAGES ============
    // Stage milestones: equally spaced across project duration
    const stageInterval = Math.max(1, Math.floor(durationMonths / (velocity.numStages + 1)));
    const stageMilestones = []; // month indices when stages become due
    for (let s = 1; s <= velocity.numStages; s++) {
      stageMilestones.push(startIdx + s * stageInterval);
    }
    const possessionMonth = totalMonths - 1; // last month

    // ============ BUILD INCOME PER MONTH ============
    const incomeMonths = new Array(totalMonths).fill(0).map((_, i) => ({
      index: i,
      downPayment: 0,
      regularBooking: 0,
      regularStages: 0,
      postBooking: 0,
      postStages: 0,
      possession: 0,
      total: 0,
      cumulative: 0,
    }));

    // 1. Down Payment: Full realization at project start
    if (startIdx < totalMonths) {
      incomeMonths[startIdx].downPayment = dpRevenue;
    }

    // 2. Regular Sales: Bookings spread across first N months
    for (let m = 0; m < regMonths && (startIdx + m) < totalMonths; m++) {
      const monthIdx = startIdx + m;
      const unitsSoldThisMonth = regPerMonth; // sqft sold
      const bookingValue = unitsSoldThisMonth * regRevenuePerUnit;

      // 45% at booking (within 2 months - we put it in the booking month for simplicity)
      const bookingPayment = bookingValue * (velocity.bookingPct / 100);
      // Spread booking payment: 50% immediately, 50% next month
      incomeMonths[monthIdx].regularBooking += bookingPayment * 0.5;
      if (monthIdx + 1 < totalMonths) {
        incomeMonths[monthIdx + 1].regularBooking += bookingPayment * 0.5;
      }

      // Stage payments: for units sold in this month, each stage triggers at milestone
      const stagePayment = bookingValue * (velocity.stagePct / 100);
      stageMilestones.forEach(sm => {
        if (sm > monthIdx && sm < totalMonths) {
          // Only if stage is after booking
          incomeMonths[sm].regularStages += stagePayment;
        }
      });

      // Possession payment
      if (possessionMonth < totalMonths) {
        incomeMonths[possessionMonth].possession += bookingValue * (velocity.possessionPct / 100);
      }
    }

    // 3. Post-Regular Sales: Quarterly bookings after regular period
    let postSoldSoFar = 0;
    for (let q = 0; q < postQuarters && postSoldSoFar < postArea; q++) {
      const qStartMonth = postStartMonth + q * 3;
      if (qStartMonth >= totalMonths) break;

      const unitsSoldThisQ = Math.min(postPerQuarter, postArea - postSoldSoFar);
      postSoldSoFar += unitsSoldThisQ;
      const bookingValue = unitsSoldThisQ * postRate;

      // At booking: 45% + any stages already due
      let dueAtBooking = bookingValue * (velocity.bookingPct / 100);
      // Check which stages have already passed
      stageMilestones.forEach(sm => {
        if (sm <= qStartMonth) {
          dueAtBooking += bookingValue * (velocity.stagePct / 100);
        }
      });

      // Spread over 2 months
      incomeMonths[qStartMonth].postBooking += dueAtBooking * 0.5;
      if (qStartMonth + 1 < totalMonths) {
        incomeMonths[qStartMonth + 1].postBooking += dueAtBooking * 0.5;
      }

      // Future stages
      stageMilestones.forEach(sm => {
        if (sm > qStartMonth && sm < totalMonths) {
          incomeMonths[sm].postStages += bookingValue * (velocity.stagePct / 100);
        }
      });

      // Possession
      if (possessionMonth < totalMonths) {
        incomeMonths[possessionMonth].possession += bookingValue * (velocity.possessionPct / 100);
      }
    }

    // Calculate totals
    let runningIncome = 0;
    incomeMonths.forEach(m => {
      m.total = m.downPayment + m.regularBooking + m.regularStages + m.postBooking + m.postStages + m.possession;
      runningIncome += m.total;
      m.cumulative = runningIncome;
    });

    // Build quarterly income
    const incomeQuarters = [];
    for (let qi = 0; qi < totalMonths; qi += 3) {
      const qMs = incomeMonths.slice(qi, Math.min(qi + 3, totalMonths));
      incomeQuarters.push({
        label: qi < preProjectMonths ? "Pre-Project" : `Q${Math.ceil((qi - preProjectMonths + 1) / 3)}`,
        downPayment: qMs.reduce((s, m) => s + m.downPayment, 0),
        regularBooking: qMs.reduce((s, m) => s + m.regularBooking, 0),
        regularStages: qMs.reduce((s, m) => s + m.regularStages, 0),
        postBooking: qMs.reduce((s, m) => s + m.postBooking, 0),
        postStages: qMs.reduce((s, m) => s + m.postStages, 0),
        possession: qMs.reduce((s, m) => s + m.possession, 0),
        total: qMs.reduce((s, m) => s + m.total, 0),
        cumulative: qMs[qMs.length - 1].cumulative,
      });
    }

    const totalIncome = runningIncome;
    const totalGrossRevenue = dpRevenue + regRevenue + postRevenue;
    const unsoldArea = developerArea * (velocity.postReservePct / 100);
    const unsoldValue = unsoldArea * avgRate;

    return {
      incomeMonths, incomeQuarters, totalIncome, totalGrossRevenue,
      dpArea, dpRate, dpRevenue, regArea, regRate, regRevenue,
      postArea, postRate, postRevenue, unsoldArea, unsoldValue,
      developerArea, avgRate, remainingPct, soldPct,
      stageMilestones: stageMilestones.map(s => s - preProjectMonths + 1),
    };
  }, [plData, formData, expenseMonths, preProjectMonths, velocity]);

  if (!incomeData || !expenseQuarters) return null;

  const { incomeQuarters } = incomeData;

  return (
    <div className="space-y-6">
      {/* ============ SALE VELOCITY FORM ============ */}
      <Card className="rounded-sm shadow-sm border-green-500/50" data-testid="sale-velocity-form">
        <CardHeader className="bg-green-50 border-b">
          <CardTitle className="font-heading tracking-tight text-green-800">Sale Velocity & Income Schedule</CardTitle>
          <CardDescription>Configure sales phasing and payment realization schedule</CardDescription>
        </CardHeader>
        <CardContent className="pt-4 space-y-5">
          {/* Summary Stats */}
          <div className="grid grid-cols-4 gap-3 text-xs">
            <div className="p-2 bg-blue-50 rounded-sm text-center">
              <p className="text-muted-foreground">Developer Area</p>
              <p className="font-mono font-semibold">{Math.round(incomeData.developerArea).toLocaleString('en-IN')} sft</p>
            </div>
            <div className="p-2 bg-blue-50 rounded-sm text-center">
              <p className="text-muted-foreground">Avg Rate</p>
              <p className="font-mono font-semibold">₹{Math.round(incomeData.avgRate).toLocaleString('en-IN')}/sft</p>
            </div>
            <div className="p-2 bg-green-50 rounded-sm text-center">
              <p className="text-muted-foreground">Total Sold</p>
              <p className="font-mono font-semibold">{incomeData.soldPct}%</p>
            </div>
            <div className="p-2 bg-amber-50 rounded-sm text-center">
              <p className="text-muted-foreground">Unsold / Reserved</p>
              <p className="font-mono font-semibold">{velocity.postReservePct}% ({formatINR(incomeData.unsoldValue)})</p>
            </div>
          </div>

          <Separator />

          {/* Phase 1: Down Payment */}
          <div className="space-y-2">
            <h4 className="font-semibold text-xs uppercase tracking-wider text-blue-700">Phase 1: Down Payment Sales</h4>
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">% of Inventory</Label>
                <Input type="number" value={velocity.downPaymentPct} onChange={e => handleChange('downPaymentPct', e.target.value)}
                  className="h-8 text-xs rounded-sm" data-testid="dp-pct" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Discount on Rate (%)</Label>
                <Input type="number" value={velocity.downPaymentDiscount} onChange={e => handleChange('downPaymentDiscount', e.target.value)}
                  className="h-8 text-xs rounded-sm" data-testid="dp-discount" />
              </div>
              <div className="p-2 bg-blue-50 rounded-sm text-xs text-center">
                <p className="text-muted-foreground">Area</p>
                <p className="font-mono font-semibold">{Math.round(incomeData.dpArea).toLocaleString('en-IN')} sft</p>
              </div>
              <div className="p-2 bg-green-50 rounded-sm text-xs text-center">
                <p className="text-muted-foreground">Revenue</p>
                <p className="font-mono font-semibold text-green-700">{formatINR(incomeData.dpRevenue)}</p>
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground">Full amount realized at project start. Rate: ₹{Math.round(incomeData.dpRate).toLocaleString('en-IN')}/sft</p>
          </div>

          <Separator />

          {/* Phase 2: Regular Sales */}
          <div className="space-y-2">
            <h4 className="font-semibold text-xs uppercase tracking-wider text-green-700">Phase 2: Regular Sales (First {velocity.regularMonths} months)</h4>
            <div className="grid grid-cols-5 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">% of Inventory</Label>
                <Input type="number" value={velocity.regularSalesPct} onChange={e => handleChange('regularSalesPct', e.target.value)}
                  className="h-8 text-xs rounded-sm" data-testid="reg-pct" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Discount (%)</Label>
                <Input type="number" value={velocity.regularDiscount} onChange={e => handleChange('regularDiscount', e.target.value)}
                  className="h-8 text-xs rounded-sm" data-testid="reg-discount" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Period (months)</Label>
                <Input type="number" value={velocity.regularMonths} onChange={e => handleChange('regularMonths', e.target.value)}
                  className="h-8 text-xs rounded-sm" data-testid="reg-months" />
              </div>
              <div className="p-2 bg-blue-50 rounded-sm text-xs text-center">
                <p className="text-muted-foreground">Area</p>
                <p className="font-mono font-semibold">{Math.round(incomeData.regArea).toLocaleString('en-IN')} sft</p>
              </div>
              <div className="p-2 bg-green-50 rounded-sm text-xs text-center">
                <p className="text-muted-foreground">Revenue</p>
                <p className="font-mono font-semibold text-green-700">{formatINR(incomeData.regRevenue)}</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Phase 3: Post Sales */}
          <div className="space-y-2">
            <h4 className="font-semibold text-xs uppercase tracking-wider text-amber-700">Phase 3: Balance Inventory (After {velocity.regularMonths} months)</h4>
            <div className="grid grid-cols-5 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Reserve Unsold (%)</Label>
                <Input type="number" value={velocity.postReservePct} onChange={e => handleChange('postReservePct', e.target.value)}
                  className="h-8 text-xs rounded-sm" data-testid="post-reserve" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Discount/Premium (%)</Label>
                <Input type="number" value={velocity.postDiscount} onChange={e => handleChange('postDiscount', e.target.value)}
                  className="h-8 text-xs rounded-sm" data-testid="post-discount" />
              </div>
              <div className="p-2 bg-blue-50 rounded-sm text-xs text-center col-span-1">
                <p className="text-muted-foreground">Sold ({incomeData.remainingPct}%)</p>
                <p className="font-mono font-semibold">{Math.round(incomeData.postArea).toLocaleString('en-IN')} sft</p>
              </div>
              <div className="p-2 bg-green-50 rounded-sm text-xs text-center">
                <p className="text-muted-foreground">Revenue</p>
                <p className="font-mono font-semibold text-green-700">{formatINR(incomeData.postRevenue)}</p>
              </div>
              <div className="p-2 bg-amber-50 rounded-sm text-xs text-center">
                <p className="text-muted-foreground">Unsold</p>
                <p className="font-mono font-semibold text-amber-700">{formatINR(incomeData.unsoldValue)}</p>
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground">Sold equally every quarter. Buyers pay 45% + any due stages at booking.</p>
          </div>

          <Separator />

          {/* Payment Schedule (Customizable) */}
          <div className="space-y-2">
            <h4 className="font-semibold text-xs uppercase tracking-wider text-purple-700">Payment Schedule (Regular & Post Sales)</h4>
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Booking Payment (%)</Label>
                <Input type="number" value={velocity.bookingPct} onChange={e => handleChange('bookingPct', e.target.value)}
                  className="h-8 text-xs rounded-sm" data-testid="pay-booking" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Per Stage (%)</Label>
                <Input type="number" value={velocity.stagePct} onChange={e => handleChange('stagePct', e.target.value)}
                  className="h-8 text-xs rounded-sm" data-testid="pay-stage" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">No. of Stages</Label>
                <Input type="number" value={velocity.numStages} onChange={e => handleChange('numStages', e.target.value)}
                  className="h-8 text-xs rounded-sm" data-testid="pay-num-stages" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Possession (%)</Label>
                <Input type="number" value={velocity.possessionPct} onChange={e => handleChange('possessionPct', e.target.value)}
                  className="h-8 text-xs rounded-sm" data-testid="pay-possession" />
              </div>
            </div>
            <div className="flex gap-2 items-center text-xs">
              <span className="text-muted-foreground">Total:</span>
              <span className={`font-mono font-semibold ${Math.abs(velocity.bookingPct + velocity.stagePct * velocity.numStages + velocity.possessionPct - 100) < 0.1 ? 'text-green-600' : 'text-red-600'}`}>
                {velocity.bookingPct}% + {velocity.numStages}×{velocity.stagePct}% + {velocity.possessionPct}% = {velocity.bookingPct + velocity.stagePct * velocity.numStages + velocity.possessionPct}%
              </span>
              <span className="text-muted-foreground ml-2">Stage milestones: M{incomeData.stageMilestones.join(', M')}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ============ COMBINED CASH FLOW TABLE ============ */}
      <Card className="rounded-sm shadow-sm" data-testid="combined-cashflow">
        <CardHeader>
          <CardTitle className="font-heading tracking-tight">Combined Cash Flow (Quarterly)</CardTitle>
          <CardDescription>Expense outflow vs income realization with net position</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="border-b-2 border-foreground/20">
                  <th className="text-left p-2 sticky left-0 bg-background z-10 min-w-[140px]">Item</th>
                  {expenseQuarters.map((q, i) => (
                    <th key={i} className="text-right p-2 min-w-[85px] whitespace-nowrap">
                      <div className="font-semibold">{q.label}</div>
                    </th>
                  ))}
                  <th className="text-right p-2 min-w-[85px] bg-accent/10 font-bold">Total</th>
                </tr>
              </thead>
              <tbody>
                {/* INCOME SECTION */}
                <tr><td colSpan={expenseQuarters.length + 2} className="p-1.5 font-semibold text-[10px] uppercase tracking-wider bg-green-50 text-green-700">Income</td></tr>
                <tr className="border-b border-muted/30 hover:bg-muted/20">
                  <td className="p-2 sticky left-0 bg-background z-10 text-muted-foreground">Down Payment</td>
                  {incomeQuarters.map((q, i) => (
                    <td key={i} className="text-right p-2 font-mono">{q.downPayment > 0 ? formatINR(q.downPayment) : '—'}</td>
                  ))}
                  <td className="text-right p-2 font-mono font-semibold bg-accent/10">{formatINR(incomeData.dpRevenue)}</td>
                </tr>
                <tr className="border-b border-muted/30 hover:bg-muted/20">
                  <td className="p-2 sticky left-0 bg-background z-10 text-muted-foreground">Regular (Booking)</td>
                  {incomeQuarters.map((q, i) => (
                    <td key={i} className="text-right p-2 font-mono">{q.regularBooking > 0 ? formatINR(q.regularBooking) : '—'}</td>
                  ))}
                  <td className="text-right p-2 font-mono font-semibold bg-accent/10">{formatINR(incomeQuarters.reduce((s, q) => s + q.regularBooking, 0))}</td>
                </tr>
                <tr className="border-b border-muted/30 hover:bg-muted/20">
                  <td className="p-2 sticky left-0 bg-background z-10 text-muted-foreground">Regular (Stages)</td>
                  {incomeQuarters.map((q, i) => (
                    <td key={i} className="text-right p-2 font-mono">{q.regularStages > 0 ? formatINR(q.regularStages) : '—'}</td>
                  ))}
                  <td className="text-right p-2 font-mono font-semibold bg-accent/10">{formatINR(incomeQuarters.reduce((s, q) => s + q.regularStages, 0))}</td>
                </tr>
                <tr className="border-b border-muted/30 hover:bg-muted/20">
                  <td className="p-2 sticky left-0 bg-background z-10 text-muted-foreground">Post Sales (Book+Due)</td>
                  {incomeQuarters.map((q, i) => (
                    <td key={i} className="text-right p-2 font-mono">{(q.postBooking + q.postStages) > 0 ? formatINR(q.postBooking + q.postStages) : '—'}</td>
                  ))}
                  <td className="text-right p-2 font-mono font-semibold bg-accent/10">{formatINR(incomeQuarters.reduce((s, q) => s + q.postBooking + q.postStages, 0))}</td>
                </tr>
                <tr className="border-b border-muted/30 hover:bg-muted/20">
                  <td className="p-2 sticky left-0 bg-background z-10 text-muted-foreground">Possession</td>
                  {incomeQuarters.map((q, i) => (
                    <td key={i} className="text-right p-2 font-mono">{q.possession > 0 ? formatINR(q.possession) : '—'}</td>
                  ))}
                  <td className="text-right p-2 font-mono font-semibold bg-accent/10">{formatINR(incomeQuarters.reduce((s, q) => s + q.possession, 0))}</td>
                </tr>
                {/* TOTAL INCOME */}
                <tr className="border-t-2 border-green-300 font-semibold bg-green-50">
                  <td className="p-2 sticky left-0 bg-green-50 z-10 text-green-700">TOTAL INCOME</td>
                  {incomeQuarters.map((q, i) => (
                    <td key={i} className="text-right p-2 font-mono text-green-700">{q.total > 0 ? formatINR(q.total) : '—'}</td>
                  ))}
                  <td className="text-right p-2 font-mono font-bold text-green-700 bg-green-100">{formatINR(incomeData.totalIncome)}</td>
                </tr>

                {/* EXPENSE ROW */}
                <tr><td colSpan={expenseQuarters.length + 2} className="p-0.5"></td></tr>
                <tr className="border-t-2 border-red-300 font-semibold bg-red-50">
                  <td className="p-2 sticky left-0 bg-red-50 z-10 text-red-700">TOTAL EXPENSES</td>
                  {expenseQuarters.map((q, i) => (
                    <td key={i} className="text-right p-2 font-mono text-red-700">{q.total > 0 ? formatINR(q.total) : '—'}</td>
                  ))}
                  <td className="text-right p-2 font-mono font-bold text-red-700 bg-red-100">{formatINR(expenseQuarters.reduce((s, q) => s + q.total, 0))}</td>
                </tr>

                {/* NET CASH FLOW */}
                <tr className="border-t-2 border-foreground/30 font-bold bg-accent/20">
                  <td className="p-2 sticky left-0 bg-accent/20 z-10">NET CASH FLOW</td>
                  {incomeQuarters.map((q, i) => {
                    const net = q.total - (expenseQuarters[i]?.total || 0);
                    return (
                      <td key={i} className={`text-right p-2 font-mono ${net >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {formatINR(net)}
                      </td>
                    );
                  })}
                  <td className={`text-right p-2 font-mono font-bold ${incomeData.totalIncome - expenseQuarters.reduce((s, q) => s + q.total, 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {formatINR(incomeData.totalIncome - expenseQuarters.reduce((s, q) => s + q.total, 0))}
                  </td>
                </tr>

                {/* CUMULATIVE NET */}
                <tr className="bg-blue-50/50">
                  <td className="p-2 sticky left-0 bg-blue-50/50 z-10 text-blue-700 font-medium">Cumulative Net</td>
                  {(() => {
                    let cumNet = 0;
                    return incomeQuarters.map((q, i) => {
                      cumNet += q.total - (expenseQuarters[i]?.total || 0);
                      return (
                        <td key={i} className={`text-right p-2 font-mono ${cumNet >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {formatINR(cumNet)}
                        </td>
                      );
                    });
                  })()}
                  <td className="text-right p-2 font-mono font-bold text-blue-700">
                    {formatINR(incomeData.totalIncome - expenseQuarters.reduce((s, q) => s + q.total, 0))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SaleVelocityForm;
